/**
 * The student import (POST /importStudents) appends its skipped-row and
 * ignored-header notes as prose to BOTH statusMessage and errorMessage, e.g.
 *
 *   "3 record(s) were skipped because the Grade is not configured for this
 *    school: 10453 (Pre-K 2), 10981 (Room7), Unknown (blank)."
 *   "The following headers were ignored because they are not supported:
 *    Allergies, Bus Route."
 *
 * These notes are informational: they are appended to errorMessage even when the
 * import succeeded, so a non-empty errorMessage must not be read as a failure.
 * This module pulls them apart so the screen can render lists instead of one
 * long sentence.
 */

// Non-greedy up to the first sentence-ending period, so the two notes cannot
// swallow each other whichever order the API appends them in.
const SKIPPED_GRADE = 'record\\(s\\)\\s+were\\s+skipped\\s+because\\s+the\\s+Grade\\s+is\\s+not\\s+configured\\s+for\\s+this\\s+school:\\s*([\\s\\S]*?)\\.(?=\\s|$)';
const IGNORED_HEADERS = 'The\\s+following\\s+headers\\s+were\\s+ignored\\s+because\\s+they\\s+are\\s+not\\s+supported:\\s*([\\s\\S]*?)\\.(?=\\s|$)';

const SKIPPED_GRADE_RE = new RegExp('(\\d+)\\s+' + SKIPPED_GRADE, 'i');
const IGNORED_HEADERS_RE = new RegExp(IGNORED_HEADERS, 'i');

// "10453 (Pre-K 2), 10981 (Room7), Unknown (blank)"
const SKIPPED_ENTRY_RE = /([^,()]+?)\s*\(([^()]*)\)/g;

const EMPTY_NOTES = {skippedCount: 0, skippedRows: [], ignoredHeaders: [], remainder: ''};

function asText(value) {
    return (typeof value === 'string') ? value : '';
}

/** Drops every occurrence of both notes and tidies up the leftover whitespace. */
export function stripImportNotes(message) {
    return asText(message)
        .replace(new RegExp('(\\d+)\\s+' + SKIPPED_GRADE, 'gi'), '')
        .replace(new RegExp(IGNORED_HEADERS, 'gi'), '')
        .replace(/\s+/g, ' ')
        .trim();
}

function parseSkippedEntries(list) {
    const rows = [];
    let entry;
    SKIPPED_ENTRY_RE.lastIndex = 0;
    while ((entry = SKIPPED_ENTRY_RE.exec(list)) !== null) {
        rows.push({studentId: entry[1].trim(), grade: entry[2].trim()});
    }
    return rows;
}

/**
 * @param statusMessage the response's statusMessage
 * @param errorMessage  the response's errorMessage (carries the same notes)
 * @returns {{skippedCount: number, skippedRows: {studentId: string, grade: string}[],
 *            ignoredHeaders: string[], remainder: string}}
 *          remainder is the summary text with both notes removed.
 */
export function parseImportNotes(statusMessage, errorMessage) {
    const status = asText(statusMessage);
    const error = asText(errorMessage);
    // Parse from both fields so a note present in only one of them is still found;
    // the non-global match keeps it to a single copy when both fields carry it.
    const combined = [status, error].filter(msg => msg.length > 0).join(' ');
    if (combined.length === 0) {
        return Object.assign({}, EMPTY_NOTES);
    }

    const skipped = combined.match(SKIPPED_GRADE_RE);
    const ignored = combined.match(IGNORED_HEADERS_RE);
    const skippedRows = skipped ? parseSkippedEntries(skipped[2]) : [];
    const ignoredHeaders = ignored
        ? ignored[1].split(',').map(header => header.trim()).filter(header => header.length > 0)
        : [];

    // Take the summary from statusMessage so the two fields' copies of it are not
    // concatenated; fall back to errorMessage when there is no statusMessage.
    const remainder = stripImportNotes(status) || stripImportNotes(error);

    return {
        skippedCount: skipped ? (parseInt(skipped[1], 10) || skippedRows.length) : 0,
        skippedRows: skippedRows,
        ignoredHeaders: ignoredHeaders,
        remainder: remainder
    };
}

/** True when the response carries notes worth showing the user. */
export function hasImportNotes(notes) {
    return !!notes && (notes.skippedRows.length > 0 || notes.ignoredHeaders.length > 0);
}



// WEBPACK FOOTER //
// ./src/components/SchoolAdmin/SetUpSchool/parseImportNotes.js